// src/front/src/store/flux/pendingPartExitActions.js
import getApiBaseUrl from "../../utils/api";

const API_URL = getApiBaseUrl();

const DEFAULT_SCANNER_SETTINGS = {
  require_operator_identification: false,
  configured: false,
  updated_by: null,
  created_at: null,
  updated_at: null,
};

const parseResponse = async (response) => {
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
};

const getRowsFromMutationPayload = (payload) =>
  Array.isArray(payload?.data?.rows) ? payload.data.rows : null;

const controlledEntryErrorResult = (
  response,
  payload,
  fallbackMessage,
) => ({
  success: false,
  data: payload?.data ?? null,
  error: payload?.error || fallbackMessage,
  code: payload?.code || null,
  field: payload?.field || null,
  status: response?.status || 0,
});

const replaceControlledEntryInStore = (
  getStore,
  setStore,
  entry,
) => {
  if (!entry?.id) return;

  const currentEntries = Array.isArray(
    getStore().controlledPartEntries,
  )
    ? getStore().controlledPartEntries
    : [];

  setStore({
    controlledPartEntries: currentEntries.map(
      (currentEntry) =>
        currentEntry.id === entry.id
          ? entry
          : currentEntry,
    ),
    controlledPartEntriesError: null,
  });
};

const pendingPartExitActions = ({ getStore, getActions, setStore }) => ({
  loadPendingPartExits: async (options = {}) => {
    const { token } = getStore();
    const { silent = false, matchingScope = "current_month" } = options;

    if (!token) {
      setStore({
        pendingPartExits: [],
        pendingPartExitsError: "Debes iniciar sesión para ver los repuestos.",
        pendingPartExitsLoading: false,
      });
      return { success: false };
    }

    if (!silent) {
      setStore({ pendingPartExitsLoading: true, pendingPartExitsError: null });
    }

    try {
      const params = new URLSearchParams({ matching_scope: matchingScope });
      const response = await fetch(`${API_URL}/api/pending-part-exits?${params}`, {
        headers: {
          Authorization: "Bearer " + token,
        },
      });
      const payload = await parseResponse(response);

      const handled = await getActions().handleApiError(response);
      if (handled) {
        if (!silent) {
          setStore({ pendingPartExitsLoading: false });
        }
        return { success: false };
      }

      if (!payload?.success) {
        const message =
          payload?.error || "No se pudo cargar el control de repuestos";
        if (!silent) {
          setStore({
            pendingPartExits: [],
            pendingPartExitsError: message,
            pendingPartExitsLoading: false,
          });
        }
        return { success: false, error: message };
      }

      setStore({
        pendingPartExits: Array.isArray(payload.data) ? payload.data : [],
        pendingPartExitsError: null,
        pendingPartExitsLoading: false,
      });
      return { success: true };
    } catch (error) {
      console.error("Pending part exits load error:", error);
      const message = "No se pudo cargar el control de repuestos";
      if (!silent) {
        setStore({
          pendingPartExits: [],
          pendingPartExitsError: message,
          pendingPartExitsLoading: false,
        });
        getActions().showToast(message, "error");
      }
      return { success: false, error: message };
    }
  },

  loadScannerSettings: async (options = {}) => {
    const { token } = getStore();
    const { silent = false } = options;

    if (!token) {
      setStore({
        scannerSettings: { ...DEFAULT_SCANNER_SETTINGS },
        scannerSettingsLoading: false,
        scannerSettingsError: null,
      });
      return {
        success: false,
        data: { ...DEFAULT_SCANNER_SETTINGS },
      };
    }

    setStore({
      scannerSettingsLoading: true,
      scannerSettingsError: null,
    });

    try {
      const response = await fetch(
        `${API_URL}/api/scanner-settings`,
        {
          headers: {
            Authorization: "Bearer " + token,
          },
        },
      );

      const payload = await parseResponse(response);

      const handled = await getActions().handleApiError(
        response,
      );

      if (handled) {
        setStore({
          scannerSettingsLoading: false,
        });
        return { success: false };
      }

      if (!response.ok || !payload?.success) {
        const message =
          payload?.error ||
          "No se pudo cargar la configuración del scanner";

        setStore({
          scannerSettingsLoading: false,
          scannerSettingsError: message,
        });

        if (!silent) {
          getActions().showToast(message, "error");
        }

        return {
          success: false,
          error: message,
        };
      }

      const settings = {
        ...DEFAULT_SCANNER_SETTINGS,
        ...(payload.data || {}),
      };

      setStore({
        scannerSettings: settings,
        scannerSettingsLoading: false,
        scannerSettingsError: null,
      });

      return {
        success: true,
        data: settings,
      };
    } catch (error) {
      console.error(
        "Scanner settings load error:",
        error,
      );

      const message =
        "No se pudo cargar la configuración del scanner";

      setStore({
        scannerSettingsLoading: false,
        scannerSettingsError: message,
      });

      if (!silent) {
        getActions().showToast(message, "error");
      }

      return {
        success: false,
        error: message,
      };
    }
  },

  updateScannerSettings: async ({
    requireOperatorIdentification,
  }) => {
    const { token } = getStore();

    if (!token) {
      return {
        success: false,
        error: "Debes iniciar sesión.",
      };
    }

    try {
      const response = await fetch(
        `${API_URL}/api/scanner-settings`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({
            require_operator_identification:
              Boolean(requireOperatorIdentification),
          }),
        },
      );

      const payload = await parseResponse(response);

      if (
        response.status === 401 ||
        response.status === 403
      ) {
        const handled =
          await getActions().handleApiError(response);

        if (handled) {
          return { success: false };
        }
      }

      if (!response.ok || !payload?.success) {
        const message =
          payload?.error ||
          "No se pudo actualizar la configuración del scanner";

        getActions().showToast(message, "error");

        return {
          success: false,
          error: message,
        };
      }

      const settings = {
        ...DEFAULT_SCANNER_SETTINGS,
        ...(payload.data || {}),
      };

      setStore({
        scannerSettings: settings,
        scannerSettingsError: null,
      });

      getActions().showToast(
        settings.require_operator_identification
          ? "Identificación de operador activada"
          : "Identificación de operador desactivada",
        "success",
      );

      return {
        success: true,
        data: settings,
      };
    } catch (error) {
      console.error(
        "Scanner settings update error:",
        error,
      );

      const message =
        "No se pudo actualizar la configuración del scanner";

      getActions().showToast(message, "error");

      return {
        success: false,
        error: message,
      };
    }
  },

  loadControlledPartEntries: async () => {
    const { token } = getStore();

    if (!token) {
      setStore({
        controlledPartEntries: [],
        controlledPartEntriesLoading: false,
        controlledPartEntriesError: "Debes iniciar sesi?n.",
      });

      return {
        success: false,
        error: "Debes iniciar sesi?n.",
      };
    }

    setStore({
      controlledPartEntriesLoading: true,
      controlledPartEntriesError: null,
    });

    try {
      const response = await fetch(
        `${API_URL}/api/controlled-part-entries`,
        {
          method: "GET",
          headers: {
            Authorization: "Bearer " + token,
          },
        },
      );

      const payload = await parseResponse(response);

      if (response.status === 401 || response.status === 403) {
        const handled = await getActions().handleApiError(response);

        if (handled) {
          setStore({
            controlledPartEntries: [],
            controlledPartEntriesLoading: false,
            controlledPartEntriesError:
              payload?.error || "Acceso denegado",
          });

          return {
            success: false,
            error: payload?.error || "Acceso denegado",
          };
        }
      }

      if (!response.ok || !payload?.success) {
        const message =
          payload?.error ||
          "No se pudieron cargar las entradas";

        setStore({
          controlledPartEntries: [],
          controlledPartEntriesLoading: false,
          controlledPartEntriesError: message,
        });

        return {
          success: false,
          error: message,
        };
      }

      const entries = Array.isArray(payload?.data?.entries)
        ? payload.data.entries
        : [];

      setStore({
        controlledPartEntries: entries,
        controlledPartEntriesLoading: false,
        controlledPartEntriesError: null,
      });

      return {
        success: true,
        data: entries,
      };
    } catch (error) {
      console.error(
        "Controlled part entries load error:",
        error,
      );

      const message =
        "No se pudieron cargar las entradas";

      setStore({
        controlledPartEntries: [],
        controlledPartEntriesLoading: false,
        controlledPartEntriesError: message,
      });

      return {
        success: false,
        error: message,
      };
    }
  },

  createControlledPartEntry: async ({
    materialId,
    quantity,
    priority = "NORMAL",
    notes = "",
    operationalDate,
  }) => {
    const { token } = getStore();

    if (!token) {
      return {
        success: false,
        error: "Debes iniciar sesi?n.",
      };
    }

    const body = {
      material_id: materialId,
      quantity,
      priority,
    };

    const normalizedNotes = String(notes || "").trim();

    if (normalizedNotes) {
      body.notes = normalizedNotes;
    }

    if (operationalDate) {
      body.operational_date = operationalDate;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/controlled-part-entries`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify(body),
        },
      );

      const payload = await parseResponse(response);

      if (response.status === 401 || response.status === 403) {
        const handled = await getActions().handleApiError(response);
        if (handled) {
          return {
            success: false,
            error: payload?.error || "Acceso denegado",
          };
        }
      }

      if (!response.ok || !payload?.success) {
        const message =
          payload?.error || "No se pudo registrar la entrada";

        getActions().showToast(message, "error");

        return {
          success: false,
          error: message,
          code: payload?.code || null,
          field: payload?.field || null,
        };
      }

      getActions().showToast(
        getStore().role === "scanner"
          ? "Entrada escaneada"
          : "Entrada registrada",
        "success",
      );

      return {
        success: true,
        data: payload.data,
      };
    } catch (error) {
      console.error(
        "Controlled part entry create error:",
        error,
      );

      const message = "No se pudo registrar la entrada";

      getActions().showToast(message, "error");

      return {
        success: false,
        error: message,
      };
    }
  },

  correctControlledPartEntry: async (
    entryId,
    {
      newQuantity,
      reason,
      expectedUpdatedAt,
    },
  ) => {
    const { token } = getStore();

    if (!token) {
      return {
        success: false,
        error: "Debes iniciar sesi?n.",
        code: null,
        field: null,
        status: 401,
      };
    }

    try {
      const response = await fetch(
        `${API_URL}/api/controlled-part-entries/${entryId}/corrections`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({
            new_quantity: newQuantity,
            reason,
            expected_updated_at: expectedUpdatedAt,
          }),
        },
      );

      const payload = await parseResponse(response);

      if (response.status === 401) {
        await getActions().handleApiError(response);

        return controlledEntryErrorResult(
          response,
          payload,
          "La sesi?n ha caducado.",
        );
      }

      if (!response.ok || !payload?.success) {
        const result = controlledEntryErrorResult(
          response,
          payload,
          "No se pudo corregir la entrada.",
        );

        if (
          response.status === 409 &&
          result.code === "ENTRY_CHANGED"
        ) {
          await getActions().loadControlledPartEntries();
        }

        return result;
      }

      replaceControlledEntryInStore(
        getStore,
        setStore,
        payload.data,
      );

      getActions().showToast(
        "Entrada corregida",
        "success",
      );

      return {
        success: true,
        data: payload.data,
        error: null,
        code: null,
        field: null,
        status: response.status,
      };
    } catch (error) {
      console.error(
        "Controlled part entry correction error:",
        error,
      );

      return {
        success: false,
        data: null,
        error: "No se pudo corregir la entrada.",
        code: null,
        field: null,
        status: 0,
      };
    }
  },

  cancelControlledPartEntry: async (
    entryId,
    {
      reason,
      expectedUpdatedAt,
    },
  ) => {
    const { token } = getStore();

    if (!token) {
      return {
        success: false,
        error: "Debes iniciar sesi?n.",
        code: null,
        field: null,
        status: 401,
      };
    }

    try {
      const response = await fetch(
        `${API_URL}/api/controlled-part-entries/${entryId}/cancel`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({
            reason,
            expected_updated_at: expectedUpdatedAt,
          }),
        },
      );

      const payload = await parseResponse(response);

      if (response.status === 401) {
        await getActions().handleApiError(response);

        return controlledEntryErrorResult(
          response,
          payload,
          "La sesi?n ha caducado.",
        );
      }

      if (!response.ok || !payload?.success) {
        const result = controlledEntryErrorResult(
          response,
          payload,
          "No se pudo anular la entrada.",
        );

        if (
          response.status === 409 &&
          result.code === "ENTRY_CHANGED"
        ) {
          await getActions().loadControlledPartEntries();
        }

        return result;
      }

      replaceControlledEntryInStore(
        getStore,
        setStore,
        payload.data,
      );

      getActions().showToast(
        "Entrada anulada",
        "success",
      );

      return {
        success: true,
        data: payload.data,
        error: null,
        code: null,
        field: null,
        status: response.status,
      };
    } catch (error) {
      console.error(
        "Controlled part entry cancellation error:",
        error,
      );

      return {
        success: false,
        data: null,
        error: "No se pudo anular la entrada.",
        code: null,
        field: null,
        status: 0,
      };
    }
  },

  loadControlledPartEntryCorrections: async (
    entryId,
  ) => {
    const { token } = getStore();

    if (!token) {
      return {
        success: false,
        data: [],
        error: "Debes iniciar sesi?n.",
        code: null,
        field: null,
        status: 401,
      };
    }

    try {
      const response = await fetch(
        `${API_URL}/api/controlled-part-entries/${entryId}/corrections`,
        {
          method: "GET",
          headers: {
            Authorization: "Bearer " + token,
          },
        },
      );

      const payload = await parseResponse(response);

      if (response.status === 401) {
        await getActions().handleApiError(response);

        return controlledEntryErrorResult(
          response,
          payload,
          "La sesi?n ha caducado.",
        );
      }

      if (!response.ok || !payload?.success) {
        return controlledEntryErrorResult(
          response,
          payload,
          "No se pudo cargar el historial de la entrada.",
        );
      }

      const corrections = Array.isArray(
        payload?.data?.corrections,
      )
        ? payload.data.corrections
        : [];

      return {
        success: true,
        data: corrections,
        error: null,
        code: null,
        field: null,
        status: response.status,
      };
    } catch (error) {
      console.error(
        "Controlled part entry corrections load error:",
        error,
      );

      return {
        success: false,
        data: [],
        error: "No se pudo cargar el historial de la entrada.",
        code: null,
        field: null,
        status: 0,
      };
    }
  },

  createPendingPartExit: async ({
    materialId,
    quantityExpected,
    matchingScope = "current_month",
    operationalDate,
    rfidCode,
  }) => {
    const { token } = getStore();
    const body = {
      material_id: materialId,
      quantity_expected: quantityExpected,
      matching_scope: matchingScope,
    };
    if (operationalDate) {
      body.operational_date = operationalDate;
    }

    const normalizedRfidCode = String(
      rfidCode || "",
    ).trim();

    if (normalizedRfidCode) {
      body.rfid_code = normalizedRfidCode;
    }

    try {
      const response = await fetch(`${API_URL}/api/pending-part-exits`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify(body),
      });
      const payload = await parseResponse(response);

      if (response.status === 401 || response.status === 403) {
        const handled = await getActions().handleApiError(response);
        if (handled) return { success: false };
      }

      if (!response.ok || !payload?.success) {
        const message = payload?.error || "No se pudo agregar el repuesto";
        getActions().showToast(message, "error");
        return { success: false, error: message };
      }

      const updatedRows = getRowsFromMutationPayload(payload);
      if (updatedRows) {
        setStore({
          pendingPartExits: updatedRows,
          pendingPartExitsError: null,
          pendingPartExitsLoading: false,
        });
      } else {
        await getActions().loadPendingPartExits({ matchingScope });
      }
      getActions().showToast(
        getStore().role === "scanner" ? "Escaneo registrado" : "Repuesto agregado",
        "success",
      );
      return { success: true, data: payload.data };
    } catch (error) {
      console.error("Pending part exit create error:", error);
      getActions().showToast("No se pudo agregar el repuesto", "error");
      return { success: false };
    }
  },

  deletePendingPartExit: async (id) => {
    const { token } = getStore();

    try {
      const response = await fetch(`${API_URL}/api/pending-part-exits/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: "Bearer " + token,
        },
      });
      const payload = await parseResponse(response);

      if (response.status === 401 || response.status === 403) {
        const handled = await getActions().handleApiError(response);
        if (handled) return { success: false };
      }

      if (!response.ok || !payload?.success) {
        const message = payload?.error || "No se pudo borrar el registro";
        getActions().showToast(message, "error");
        return { success: false, error: message };
      }

      setStore({
        pendingPartExits: getStore().pendingPartExits.filter(
          (item) => item.id !== id,
        ),
      });
      getActions().showToast("Registro borrado", "success");
      return { success: true };
    } catch (error) {
      console.error("Pending part exit delete error:", error);
      getActions().showToast("No se pudo borrar el registro", "error");
      return { success: false };
    }
  },

  undoLatestPendingPartExit: async (options = {}) => {
    const { token } = getStore();
    const { matchingScope = "current_month", quantityToUndo = 1 } = options;

    try {
      const response = await fetch(`${API_URL}/api/pending-part-exits/undo-latest`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify({
          quantity_to_undo: quantityToUndo,
          matching_scope: matchingScope,
        }),
      });
      const payload = await parseResponse(response);

      if (response.status === 401 || response.status === 403) {
        const handled = await getActions().handleApiError(response);
        if (handled) return { success: false };
      }

      if (!response.ok || !payload?.success) {
        const message = payload?.error || "No se pudo deshacer el escaneo";
        getActions().showToast(message, "error");
        return { success: false, error: message };
      }

      const updatedRows = getRowsFromMutationPayload(payload);
      if (updatedRows) {
        setStore({
          pendingPartExits: updatedRows,
          pendingPartExitsError: null,
          pendingPartExitsLoading: false,
        });
      } else {
        await getActions().loadPendingPartExits({ matchingScope });
      }
      getActions().showToast("Escaneo deshecho", "success");
      return { success: true, data: payload.data };
    } catch (error) {
      console.error("Pending part exit undo latest error:", error);
      getActions().showToast("No se pudo deshacer el escaneo", "error");
      return { success: false };
    }
  },

  undoPendingPartExit: async (id, options = {}) => {
    const { token } = getStore();
    const { matchingScope = "current_month", quantityToUndo = 1 } = options;

    try {
      const response = await fetch(`${API_URL}/api/pending-part-exits/${id}/undo`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify({
          quantity_to_undo: quantityToUndo,
          matching_scope: matchingScope,
        }),
      });
      const payload = await parseResponse(response);

      if (response.status === 401 || response.status === 403) {
        const handled = await getActions().handleApiError(response);
        if (handled) return { success: false };
      }

      if (!response.ok || !payload?.success) {
        const message = payload?.error || "No se pudo devolver el registro";
        getActions().showToast(message, "error");
        return { success: false, error: message };
      }

      const updatedRows = getRowsFromMutationPayload(payload);
      if (updatedRows) {
        setStore({
          pendingPartExits: updatedRows,
          pendingPartExitsError: null,
          pendingPartExitsLoading: false,
        });
      } else {
        await getActions().loadPendingPartExits({ matchingScope });
      }
      getActions().showToast("Devolución aplicada", "success");
      return { success: true, data: payload.data };
    } catch (error) {
      console.error("Pending part exit undo error:", error);
      getActions().showToast("No se pudo devolver el registro", "error");
      return { success: false };
    }
  },

  revalidatePendingPartExits: async (options = {}) => {
    const { token } = getStore();
    const { matchingScope = "current_month" } = options;

    try {
      const response = await fetch(
        `${API_URL}/api/pending-part-exits/revalidate`,
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + token,
          },
        },
      );
      const payload = await parseResponse(response);

      const handled = await getActions().handleApiError(response);
      if (handled) return { success: false };

      if (!response.ok || !payload?.success) {
        const message = payload?.error || "No se pudo revalidar";
        getActions().showToast(message, "error");
        return { success: false, error: message };
      }

      await getActions().loadPendingPartExits({ matchingScope });
      getActions().showToast("Revalidación completada", "success");
      return { success: true, data: payload.data };
    } catch (error) {
      console.error("Pending part exits revalidate error:", error);
      getActions().showToast("No se pudo revalidar", "error");
      return { success: false };
    }
  },
});

export default pendingPartExitActions;
